import './SubscriptionsPage.css';
import PageScaffold from '../../components/PageScaffold';
import { useContext, useEffect, useState } from 'react';

import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExportOutlined,
  PlusOutlined,
  ReloadOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';

import SubscriptionsList from '../../components/@shared/SubscriptionsList';
import { formatNumber } from '@leedpulse-monorepo/utils';
import { UserRole, EProductType, productTypeAsString } from '@leedpulse-monorepo/enums';
import PageScaffoldContext from '../../contexts/ui/PageScaffoldContext';
import { Button, Collapse, Flex, Radio, Select, Tooltip } from 'antd';
import { OrderByDirection } from 'firebase/firestore';
import {
  SubscriptionFilters,
  SubscriptionStatus,
} from '../../../domain/use_cases/subscriptions/getSubscriptionsWithFilters';
import UsersContext from '../../contexts/data/UsersContext';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import PlansContext from '../../contexts/data/PlansContext';
import { useNavigate } from 'react-router-dom';
import ExportSubscriptionsStatsModal from '../../components/subscriptions_page/ExportSubscriptionsStatsModal';
import SubscriptionDataSource, { subscriptionDataSourceAsString } from '../../../domain/enums/subscription/subscription_datasource/SubscriptionDataSource';
import searchSubscriptions from '../../../domain/use_cases/subscriptions/searchSubscriptions';
import { ISubscriptionView } from '../../../domain/interfaces/ISubscriptionView';

export const SUBSCRIPTIONS_PAGE_PATH = '/subscriptions';
export const SUBSCRIPTIONS_PAGE_TITLE = 'Abonnements';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SubscriptionsPageProps { }

const SubscriptionsPage: React.FC<SubscriptionsPageProps> = (_) => {
  const pageScaffoldContext = useContext(PageScaffoldContext);
  const usersContext = useContext(UsersContext);
  const plansContext = useContext(PlansContext);
  const authContext = useContext(AuthenticationContext);
  const [exportStatsModal, setExportStatsModal] = useState<boolean>(false);

  const [subscriptions, setSubscriptions] = useState<ISubscriptionView[]>([]);
  const [page, setPage] = useState<number>(0);
  const [nbHits, setNbHits] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hitsPerPage, setHitsPerPage] = useState<number>(20);

  const [sortSelected, setSortSelected] = useState(() => {
    const sortSelectedSaved = localStorage.getItem('subscriptionOrderBy');
    return sortSelectedSaved ? sortSelectedSaved : undefined;
  });
  const [orderByDirection, setOrderByDirection] = useState(() => {
    const orderByDirection = localStorage.getItem('subscriptionOrderDirection');
    return orderByDirection
      ? (orderByDirection as OrderByDirection)
      : ('desc' as OrderByDirection);
  });
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem('subscriptionFilters');
    return savedFilters
      ? JSON.parse(savedFilters)
      : ({
        employeesInCharge: [authContext.authentication?.id],
        status: 'active',
      } as SubscriptionFilters);
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState(() => {
    const subscriptionStatusSaved = localStorage.getItem('subscriptionStatus');
    return subscriptionStatusSaved
      ? subscriptionStatusSaved
      : ('active' as SubscriptionStatus);
  });
  const [isFilterOn, setIsFilterOn] = useState(true);

  const pageScaffoldContentRef = pageScaffoldContext?.contentRef;
  const navigate = useNavigate();

  useEffect(() => {
    usersContext.fetchRoles([
      UserRole.admin,
      UserRole.maintainer,
      UserRole.owner,
    ]);
    plansContext.fetch();
  }, []);

  useEffect(() => {
    localStorage.setItem('subscriptionFilters', JSON.stringify(filters));
    localStorage.setItem('subscriptionStatus', subscriptionStatus);
  }, [filters, subscriptionStatus]);

  useEffect(() => {
    if (sortSelected) localStorage.setItem('subscriptionOrderBy', sortSelected);
    else localStorage.removeItem('subscriptionOrderBy');
  }, [sortSelected]);

  useEffect(() => {
    localStorage.setItem('subscriptionOrderDirection', orderByDirection);
  }, [orderByDirection]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchSubscription(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters, sortSelected, orderByDirection]);

  async function searchSubscription(pageToFetch = 0, newHitsPerPage = hitsPerPage) {
    setIsLoading(true);
    try {
      const algoliaFilters: Record<string, unknown> = {};
      if (sortSelected) {
        algoliaFilters.sort = sortSelected;
        algoliaFilters.orderDirection = orderByDirection;
      }
      if (filters.status) algoliaFilters.status = filters.status;
      if (filters.subStatus) algoliaFilters.subStatus = filters.subStatus;
      if (filters.plans?.length) algoliaFilters.plans = filters.plans;
      if (filters.employeesInCharge?.length) algoliaFilters.employeesInCharge = filters.employeesInCharge;
      if (filters.datasource?.length) algoliaFilters.datasource = filters.datasource;
      if (filters.products?.length) algoliaFilters.products = filters.products;

      const useCaseResponse = await searchSubscriptions(
        authContext.authentication,
        filters.query ?? '',
        pageToFetch,
        newHitsPerPage,
        algoliaFilters,
      );
      setSubscriptions(useCaseResponse.hits);
      setNbHits(useCaseResponse.nbHits);
      setPage(pageToFetch);
    } catch (err) {
      console.error('[Algolia] erreur :', err);
    } finally {
      setIsLoading(false);
    }
  }

  const sortOptions = [
    { value: 'created', label: 'Date de création' },
    { value: 'plan_end', label: "Date de fin d'abonnement" },
    { value: 'name', label: 'Nom' },
  ];

  const handleScrollToTop = () => {
    pageScaffoldContentRef?.current?.scrollTo(0, 0);
  };

  const handleClearAllFilters = () => {
    setSubscriptionStatus('active');
    setFilters({
      status: 'active',
      employeesInCharge: [authContext.authentication?.id],
    });
    setSortSelected(undefined);
    setOrderByDirection('desc');
  };

  const updateFilters = (filterName: keyof typeof filters, value: any) => {
    setFilters((prevFilters: any) => ({ ...prevFilters, [filterName]: value }));
  };

  const handleFiltersChanged = () => {
    const filterHaveChanged =
      JSON.stringify(filters) !==
      JSON.stringify({
        status: 'active',
        employeesInCharge: [authContext.authentication?.id],
      }) ||
      sortSelected !== undefined ||
      orderByDirection !== 'desc';
    setIsFilterOn(filterHaveChanged);
  };

  return (
    <PageScaffold
      title={
        SUBSCRIPTIONS_PAGE_TITLE +
        ' (' +
        formatNumber(nbHits) +
        ')'
      }
      subtitle="Gérez les abonnements de l'application"
      breadcrumbItems={[
        {
          title: 'Abonnements',
        },
      ]}
      controlsSettings={{
        searchLabel: 'Recherche par nom du client',
        searchDefaultValue: filters.query ?? '',
        onSearch: (value) => updateFilters('query', value),
        isLoading: false,
        onSearchCancel: () => updateFilters('query', ''),
        actionButtons: [
          {
            label: 'Nouvel abonnement',
            type: 'primary',
            props: {
              icon: <PlusOutlined />,
              onClick: () => navigate('/users/new?type=subscription'),
            },
          },
          {
            label: 'Exporter des stats',
            type: 'primary',
            props: {
              icon: <ExportOutlined />,
              onClick: () => setExportStatsModal(true),
            },
          },
          {
            label: 'Rafraîchir',
            type: 'primary',
            props: {
              icon: <ReloadOutlined />,
              onClick: () => searchSubscription(page),
            },
          },
        ],
      }}
    >
      <Collapse
        collapsible="icon"
        items={[
          {
            key: '1',
            styles: {
              header: {
                display: 'flex',
                alignItems: 'center',
              },
            },
            label: (
              <div className="subscription-page__filter-bar">
                <h4>Filtres</h4>
                <Radio.Group
                  value={subscriptionStatus}
                  defaultValue={subscriptionStatus}
                >
                  <Radio.Button
                    value="active"
                    onClick={() => {
                      setSubscriptionStatus('active');
                      updateFilters('status', 'active');
                    }}
                  ><CheckCircleOutlined style={{ marginRight: 6, color: 'green' }} /> Actifs</Radio.Button>
                  <Radio.Button
                    value="expired"
                    onClick={() => {
                      setSubscriptionStatus('expired');
                      updateFilters('status', 'expired');
                    }}
                  ><CloseCircleOutlined style={{ marginRight: 6, color: 'red' }} /> Expirés</Radio.Button>
                </Radio.Group>
                <Tooltip title="Trier par">
                  <Select
                    className="subscription-page__sort-select"
                    placeholder="Trier par"
                    allowClear
                    value={sortSelected}
                    options={sortOptions}
                    onChange={(value) => setSortSelected(value)}
                  />
                </Tooltip>
                {sortSelected ? (
                  orderByDirection === 'asc' ? (
                    <Tooltip title="Ordre croissant">
                      <Button
                        icon={<SortAscendingOutlined />}
                        onClick={() => setOrderByDirection('desc')}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Ordre décroissant">
                      <Button
                        icon={<SortDescendingOutlined />}
                        onClick={() => setOrderByDirection('asc')}
                      />
                    </Tooltip>
                  )
                ) : null}
                {isFilterOn ? (
                  <Tooltip title="Réinitialiser les filtres">
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleClearAllFilters}
                    />
                  </Tooltip>
                ) : null}
              </div>
            ),
            children: (
              <div className="subscription-page__filter-bar">
                <Tooltip title="Géré par">
                  <Select
                    className="subscription-page__employee-select"
                    placeholder="Géré par"
                    allowClear
                    mode="multiple"
                    maxTagCount={'responsive'}
                    value={filters?.employeesInCharge}
                    options={usersContext.items.map((user) => ({
                      value: user.uid,
                      label: `${user.firstname} ${user.lastname}`,
                    }))}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => updateFilters('employeesInCharge', value)}
                  />
                </Tooltip>
                <Tooltip title="Plans">
                  <Select
                    className="subscription-page__domain-select"
                    style={{ width: 250 }}
                    placeholder="Plans"
                    mode="multiple"
                    value={filters.plans}
                    allowClear
                    maxTagCount={'responsive'}
                    options={plansContext.items.map((plan) => ({
                      label: plan.name,
                      value: plan.id,
                    }))}
                    onChange={(value) => updateFilters('plans', value)}
                  />
                </Tooltip>
                <Tooltip title="Statut">
                  <Select
                    className="subscription-page__domain-select"
                    style={{ width: 250 }}
                    placeholder="Statut"
                    allowClear
                    value={filters.subStatus}
                    options={[
                      { label: <Flex align="center" gap={6}><CheckCircleOutlined style={{ color: 'green' }} /> Prêt</Flex>, value: 'ready' },
                      { label: <Flex align="center" gap={6}><ClockCircleOutlined style={{ color: 'orange' }} /> Non finalisé</Flex>, value: 'not_ready' },
                    ]}
                    onChange={(value) => updateFilters('subStatus', value)}
                  />
                </Tooltip>
                <Tooltip title="CRM utilisé">
                  <Select
                    className="subscription-page__domain-select"
                    style={{ width: 250 }}
                    placeholder="🖥️ CRM utilisé"
                    mode="multiple"
                    value={filters.datasource}
                    allowClear
                    maxTagCount={'responsive'}
                    options={Object.values(SubscriptionDataSource)
                      .map((crm) => ({
                        label: subscriptionDataSourceAsString(crm),
                        value: crm,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label))}
                    onChange={(value) => updateFilters('datasource', value)}
                  />
                </Tooltip>
                <Tooltip title="Produit">
                  <Select
                    className="subscription-page__domain-select"
                    style={{ width: 250 }}
                    placeholder="Produit"
                    mode="multiple"
                    value={filters.products}
                    allowClear
                    maxTagCount={'responsive'}
                    options={[
                      {
                        label: productTypeAsString(EProductType.LEEDPULSE),
                        value: EProductType.LEEDPULSE,
                      },
                      {
                        label: productTypeAsString(EProductType.LEEDFLOW),
                        value: EProductType.LEEDFLOW,
                      },
                    ]}
                    onChange={(value) => updateFilters('products', value)}
                  />
                </Tooltip>
              </div>
            ),
          },
        ]}
        style={{ marginBottom: '1rem', backgroundColor: 'var(--surface-color)' }}
        defaultActiveKey={1}
        expandIconPosition="end"
      />
      <SubscriptionsList
        subscriptions={subscriptions}
        loading={isLoading}
        page={page}
        nbHits={nbHits}
        hitsPerPage={hitsPerPage}
        onPageChange={(p) => searchSubscription(p)}
        onScrollToTop={handleScrollToTop}
        onPageSizeChange={(newPageSize) => {
          setHitsPerPage(newPageSize);
          searchSubscription(0, newPageSize);
        }}
      />

      <ExportSubscriptionsStatsModal
        open={exportStatsModal}
        onCancel={() => setExportStatsModal(false)}
      />
    </PageScaffold>
  );
};

export default SubscriptionsPage;
